import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import "../../../assets/css/colors.css";
import { useLanguage } from "context/LanguageProvider";


const AVLSearch = () => {
    const { darkMode } = useDarkMode();
    const [currentLevel, setCurrentLevel] = useState(0);
    const [searchPath, setSearchPath] = useState([]);
    const [draggedNode, setDraggedNode] = useState(null);
    const [message, setMessage] = useState("");
    const [gameState, setGameState] = useState("playing");
    const [lives, setLives] = useState(3);
    const [completedLevels, setCompletedLevels] = useState(0);
    const [showGameOver, setShowGameOver] = useState(false);
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();
    const [showGameWon, setShowGameWon] = useState(false)


    const gameLevels = [
        {
            level: 1,
            name: selLanguage ? "Αρχάριο" : "Beginner",
            tree: [
                { value: 20, balance: 1 },  // root - index 0
                { value: 10, balance: 0 }, // left - index 1
                { value: 30, balance: -1 }, // right - index 2
                { value: 5, balance: 0 },   // left-left - index 3
                { value: 15, balance: 0 },  // left-right - index 4
                { value: 25, balance: 0 },  // right-left - index 5
                null                        // right-right - index 6
            ],
            target: 25,
            description: selLanguage
                ? "Βρες το στοιχείο **25** στο AVL δέντρο. Ξεκίνα από τη ρίζα και ακολούθησε τη σωστή διαδρομή BST!"
                : "Find element **25** in the AVL tree. Start from the root and follow the correct BST path!"
        },
        {
            level: 2,
            name: selLanguage ? "Μέτριο" : "Intermediate",
            tree: [
                { value: 25, balance: 0 },  // root - index 0
                { value: 15, balance: 1 },  // left - index 1
                { value: 40, balance: 0 },  // right - index 2
                { value: 10, balance: 0 },  // left-left - index 3
                { value: 20, balance: -1 }, // left-right - index 4
                { value: 35, balance: 0 },  // right-left - index 5
                { value: 50, balance: 0 },  // right-right - index 6
                { value: 8, balance: 0 },   // left-left-left - index 7
                { value: 12, balance: 0 },  // left-left-right - index 8
                { value: 18, balance: 0 },  // left-right-left - index 9
                null, null, null, null, null // remaining positions
            ],
            target: 18,
            description: selLanguage
                ? "Βρες το στοιχείο **18** στο μεγαλύτερο AVL δέντρο. Περισσότεροι κόμβοι, περισσότερες επιλογές!"
                : "Find element **18** in the larger AVL tree. More nodes, more choices!"
        },
        {
            level: 3,
            name: selLanguage ? "Προχωρημένο" : "Advanced",
            tree: [
                { value: 30, balance: 0 },  // root - index 0
                { value: 15, balance: 1 },  // left - index 1
                { value: 45, balance: -1 }, // right - index 2
                { value: 8, balance: 0 },   // left-left - index 3
                { value: 22, balance: 0 },  // left-right - index 4
                { value: 38, balance: 0 },  // right-left - index 5
                { value: 60, balance: -1 }, // right-right - index 6
                { value: 3, balance: 0 },   // left-left-left - index 7
                { value: 12, balance: 0 },  // left-left-right - index 8
                { value: 18, balance: 0 },  // left-right-left - index 9
                { value: 26, balance: 0 },  // left-right-right - index 10
                { value: 35, balance: 0 },  // right-left-left - index 11
                { value: 42, balance: 0 },  // right-left-right - index 12
                { value: 52, balance: 0 },  // right-right-left - index 13
                { value: 70, balance: 0 }   // right-right-right - index 14
            ],
            target: 57,
            description: selLanguage
                ? "Βρες το στοιχείο **57** στο πολύπλοκο AVL δέντρο. Μεγάλη πρόκληση αναζήτησης!"
                : "Find element **57** in the complex AVL tree. A big search challenge!"
        }
    ];

    const currentLevelData = gameLevels[currentLevel];



    const handlePlayAgain = () => {
        resetGame();
    };

    useEffect(() => {
        initializeLevel();
    }, [currentLevel]);

    const initializeLevel = () => {
        setSearchPath([]);
        setMessage("");
        setGameState("playing");
    };

    const getCorrectSearchPath = (target) => {
        const path = [];
        let currentIndex = 0;

        while (currentIndex < 15 && currentLevelData.tree[currentIndex]) {
            const node = currentLevelData.tree[currentIndex];
            path.push(node.value);

            if (node.value === target) {
                break;
            } else if (target < node.value) {
                currentIndex = currentIndex * 2 + 1; // Go left
            } else {
                currentIndex = currentIndex * 2 + 2; // Go right
            }
        }

        return path;
    };

    const handleDragStart = (e, nodeValue) => {
        e.preventDefault = false; // Ensure dragging works
        setDraggedNode(nodeValue);
        e.dataTransfer.setData("text/plain", nodeValue.toString());
        e.dataTransfer.effectAllowed = "move";

        // Add visual feedback
        e.target.style.opacity = "0.5";
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = "1";
        setDraggedNode(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDropOnPath = (e) => {
        e.preventDefault();
        const nodeValue = parseInt(e.dataTransfer.getData("text/plain"));

        if (!nodeValue || searchPath.includes(nodeValue)) return;

        setSearchPath(prev => [...prev, nodeValue]);
    };

    const handleRemoveFromPath = (nodeValue) => {
        setSearchPath(prev => prev.filter(val => val !== nodeValue));
    };



    const checkSolution = () => {
        const correctPath = getCorrectSearchPath(currentLevelData.target);

        if (JSON.stringify(searchPath) === JSON.stringify(correctPath)) {
            if (currentLevel === gameLevels.length - 1) {
                setGameState("completed");
                setMessage(selLanguage ? " Συγχαρητήρια! Ολοκλήρωσες όλα τα στάδια του AVL Search! Είσαι εμπειρογνώμονας!" : "Congratulations! You've completed all levels of AVL Search! You're a pro!");
                setShowGameWon(true);
                setCompletedLevels(gameLevels.length);
            } else {
                setGameState("levelCompleted");
                setMessage(selLanguage ? `Μπράβο! Ολοκλήρωσες το στάδιο ${currentLevel + 1}. Προχωράς στο επόμενο!` : `Excellent! You've completed level ${currentLevel + 1}. Moving to the next level!`);
                setCompletedLevels(currentLevel + 1);
                setTimeout(() => {
                    setCurrentLevel(prev => prev + 1);
                }, 2000);
            }
        } else {
            const newLives = lives - 1; // Υπολόγισε τις νέες ζωές πρώτα
            setLives(newLives);

            if (newLives <= 0) { // Χρήση των νέων ζωών
                setGameState("lost");
                setShowGameOver(true);
                setMessage(selLanguage ? ` Έχασες όλες τις ζωές! Η σωστή διαδρομή για το ${currentLevelData.target} είναι: ${correctPath.join(' → ')}` : ` You've lost all lives! The correct path for ${currentLevelData.target} is: ${correctPath.join(' → ')}`);
            } else {
                setMessage(selLanguage ? ` Λάθος διαδρομή! Για το ${currentLevelData.target}, προσπάθησε ξανά.` : ` Wrong path! For ${currentLevelData.target}, try again.`);
            }
        }
    };

    const resetGame = () => {
        setCurrentLevel(0);
        setSearchPath([]);
        setMessage("");
        setGameState("playing");
        setLives(3);
        setCompletedLevels(0);
        setShowGameOver(false);
        setShowGameWon(false);
    };

    const getNodePosition = (index) => {
        // Enhanced positioning for more nodes
        const positions = {
            // Level 0
            0: { x: 50, y: 8 },
            // Level 1
            1: { x: 25, y: 25 },
            2: { x: 75, y: 25 },
            // Level 2
            3: { x: 12.5, y: 42 },
            4: { x: 37.5, y: 42 },
            5: { x: 62.5, y: 42 },
            6: { x: 87.5, y: 42 },
            // Level 3
            7: { x: 6.25, y: 59 },
            8: { x: 18.75, y: 59 },
            9: { x: 31.25, y: 59 },
            10: { x: 43.75, y: 59 },
            11: { x: 56.25, y: 59 },
            12: { x: 68.75, y: 59 },
            13: { x: 81.25, y: 59 },
            14: { x: 93.75, y: 59 }
        };
        return positions[index] || { x: 50, y: 76 };
    };

    const renderNode = (index) => {
        const node = currentLevelData.tree[index];
        if (!node) return null;

        const isInPath = searchPath.includes(node.value);
        const isTarget = node.value === currentLevelData.target;
        const position = getNodePosition(index);

        return (
            <motion.div
                key={`${currentLevel}-${index}`}
                draggable={true}
                onDragStart={(e) => handleDragStart(e, node.value)}
                onDragEnd={handleDragEnd}

                style={{
                    position: "absolute",
                    top: `${position.y}%`,
                    left: `${position.x}%`,
                    transform: "translate(-50%, -50%)",
                    width: "45px",
                    height: "45px",
                    borderRadius: "50%",
                    backgroundColor: isTarget
                        ? "#ef4444"
                        : isInPath
                            ? "#6b7280"
                            : "#3b82f6",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    cursor: "grab",
                    border: isTarget
                        ? "3px solid #dc2626"
                        : isInPath
                            ? "2px solid #4b5563"
                            : "2px solid #1d4ed8",
                    boxShadow: isTarget
                        ? "0 0 15px rgba(239, 68, 68, 0.6)"
                        : "0 2px 8px rgba(0,0,0,0.2)",
                    zIndex: 10,
                    fontSize: "12px",
                    userSelect: "none"
                }}
                title={isTarget ? selLanguage ? ` Στόχος: ${node.value}` : ` Target ${node.value}`
                    : selLanguage ? `Κόμβος: ${node.value}` : ` Node: ${node.value}`}
            >
                {node.value}
            </motion.div>
        );
    };

    const renderEdge = (fromIndex, toIndex) => {
        if (!currentLevelData.tree[fromIndex] || !currentLevelData.tree[toIndex]) return null;

        const fromPos = getNodePosition(fromIndex);
        const toPos = getNodePosition(toIndex);

        // Calculate edge from bottom of parent to top of child
        const fromX = fromPos.x;
        const fromY = fromPos.y + 3; // Add offset for bottom of node
        const toX = toPos.x;
        const toY = toPos.y - 3; // Subtract offset for top of node

        return (
            <line
                key={`edge-${fromIndex}-${toIndex}`}
                x1={`${fromX}%`}
                y1={`${fromY}%`}
                x2={`${toX}%`}
                y2={`${toY}%`}
                stroke="#8fb2c2"
                strokeWidth="2"
                opacity="0.8"
                markerEnd="url(#arrowhead)"
            />
        );
    };

    const renderAllEdges = () => {
        const edges = [];
        for (let i = 0; i < 7; i++) {
            const leftChild = i * 2 + 1;
            const rightChild = i * 2 + 2;

            if (leftChild < 15 && currentLevelData.tree[i] && currentLevelData.tree[leftChild]) {
                edges.push(renderEdge(i, leftChild));
            }
            if (rightChild < 15 && currentLevelData.tree[i] && currentLevelData.tree[rightChild]) {
                edges.push(renderEdge(i, rightChild));
            }
        }
        return edges;
    };

    const formatExampleText = (text) => {
        return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    };

    useEffect(() => {
        if (message && gameState !== "levelCompleted") {
            const timer = setTimeout(() => {
                setMessage("");
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [message, gameState]);

    const containerStyle = {
        minHeight: "90vh",
        background: darkMode
            ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)"
            : "var(--lightModeGameBackground)",
        padding: "30px 10px",
        borderRadius: "10px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        overflowX: "hidden",
    };

    const pronStyle = {
        borderRadius: "20px",
        maxWidth: "770px",
        background: "linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(29, 78, 216, 0.4))",
        width: "90%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "15px 20px",
        margin: "0 auto 20px",
        color: "white",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        fontSize: "14px",
        lineHeight: "1.5"
    };

    return (
        <div className='main-content' style={containerStyle}>
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={handlePlayAgain}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={handlePlayAgain}
            />


            <div className="max-w-7xl mx-auto" style={{ width: "100%" }}>

                {/* Lives Section */}
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between", marginBottom: "5px", alignItems: "center" }}>
                    {/* Level Progress */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                        <div style={{
                            color: darkMode ? "white" : "#1e293b",
                            padding: "12px 24px",
                            borderRadius: "12px",
                            fontWeight: "700",
                            fontSize: "18px",
                            border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}>
                            {selLanguage ? `Στάδιο: ${currentLevel + 1}/${gameLevels.length}` : `Level: ${currentLevel + 1}/${gameLevels.length}`}
                        </div>

                    </div>

                    {/* Lives */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        justifyContent: "right",
                        gap: "12px"
                    }}>

                        <div style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center"
                        }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <br />

                {/* Instructions */}
                <div style={pronStyle}>
                    <div dangerouslySetInnerHTML={{ __html: formatExampleText(currentLevelData.description) }} />
                </div>

                {/* AVL Tree Visualization */}
                <div style={{
                    position: "relative",
                    maxWidth: "800px",
                    margin: "0 auto 30px auto",
                    width: "100%",
                    height: "400px",
                    background: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "20px",
                    padding: "20px",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    overflow: "visible"
                }}>
                    {/* SVG for edges */}
                    <svg style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 1,
                        overflow: "visible"
                    }}>
                        <defs>
                            <marker id="arrowhead" markerWidth="10" markerHeight="7"
                                refX="0" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#8fb2c2" />
                            </marker>
                        </defs>
                        {renderAllEdges()}
                    </svg>

                    {/* Render all nodes */}
                    {currentLevelData.tree.map((node, index) =>
                        node ? renderNode(index) : null
                    )}
                </div>

                {/* Search Path Box */}
                <div style={{
                    marginBottom: "24px",
                    background: "#8fb2c280",
                    padding: "20px 24px",
                    borderRadius: "16px",
                    border: "1px solid rgba(148, 163, 184, 0.2)",
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                    maxWidth: "800px",
                    margin: "0 auto 24px auto",
                    width: "100%"
                }}>
                    <h3 style={{
                        fontWeight: "600",
                        marginBottom: "16px",
                        color: "white",
                        textAlign: "center"
                    }}>
                        {selLanguage ? `Διαδρομή Αναζήτησης για το ${currentLevelData.target}:` : `Search Path for ${currentLevelData.target}:`}
                    </h3>
                    <div
                        onDragOver={handleDragOver}
                        onDrop={handleDropOnPath}
                        style={{
                            minHeight: "70px",
                            display: "flex",
                            gap: "12px",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            alignItems: "center",
                            background: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "12px",
                            padding: "20px",
                            border: "2px dashed rgba(255, 255, 255, 0.3)"
                        }}
                    >
                        {searchPath.length === 0 ? (
                            <span style={{ color: "rgba(255, 255, 255, 0.6)", fontStyle: "italic", fontSize: "16px" }}>
                                {selLanguage ? "Σύρε τους κόμβους με τη σειρά που θα τους επισκεφτείς..." : "Drag nodes here in the order you will visit them..."}
                            </span>
                        ) : (
                            searchPath.map((nodeValue, index) => (
                                <div key={`path-${index}`} style={{ display: "flex", alignItems: "center" }}>
                                    <motion.div
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => handleRemoveFromPath(nodeValue)}
                                        style={{
                                            width: "45px",
                                            height: "45px",
                                            borderRadius: "50%",
                                            backgroundColor: "#059669",
                                            color: "white",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontWeight: "bold",
                                            cursor: "pointer",
                                            border: "2px solid #047857",
                                            fontSize: "14px",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
                                        }}
                                        title={`Κλικ για αφαίρεση: ${nodeValue}`}
                                    >
                                        {nodeValue}
                                    </motion.div>
                                    {index < searchPath.length - 1 && (
                                        <span style={{ color: "white", margin: "0 10px", fontSize: "24px", fontWeight: "bold" }}>→</span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Messages */}
                <AnimatePresence>
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                                textAlign: "center",
                                padding: "20px",
                                borderRadius: "16px",
                                marginBottom: "20px",
                                background: gameState === "levelCompleted" || gameState === "completed"
                                    ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                    : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                                border: `2px solid ${gameState === "levelCompleted" || gameState === "completed" ? "#10b981" : "#ef4444"}`,
                                color: gameState === "levelCompleted" || gameState === "completed" ? "#059669" : "#dc2626",
                                fontWeight: 600,
                                fontSize: "1rem",
                                boxShadow: gameState === "levelCompleted" || gameState === "completed"
                                    ? "0 10px 25px rgba(16, 185, 129, 0.2)"
                                    : "0 10px 25px rgba(239, 68, 68, 0.2)",
                                maxWidth: "800px",
                                margin: "0 auto 20px auto",
                                width: "100%"
                            }}
                        >
                            {message}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Buttons */}
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    width: "100%",
                    maxWidth: "600px",
                    margin: "0 auto",
                    flexWrap: "wrap"
                }}>
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={checkSolution}
                        disabled={searchPath.length === 0}
                        style={{
                            background: searchPath.length === 0
                                ? "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
                                : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "16px",
                            padding: "14px 20px",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            cursor: searchPath.length === 0 ? "not-allowed" : "pointer",
                            boxShadow: "0 10px 25px rgba(59, 130, 246, 0.3)",
                            transition: "all 0.3s ease",
                            flex: "1 1 150px",
                            minWidth: "120px",
                            opacity: searchPath.length === 0 ? 0.6 : 1
                        }}
                    >
                        {selLanguage ? "Έλεγχος Διαδρομής" : "Check Path"}
                    </motion.button>


                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setSearchPath([])}
                        style={{
                            background: "linear-gradient(135deg, #b81e1eff 0%, #7c0505ff 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "16px",
                            padding: "14px 20px",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            boxShadow: "0 10px 25px rgba(184, 30, 30, 0.3)",
                            transition: "all 0.3s ease",
                            flex: "1 1 150px",
                            minWidth: "120px"
                        }}
                    >
                        {selLanguage ? "Επανεκκίνηση" : "Reset"}
                    </motion.button>
                </div>
            </div>
        </div>
    );
};

export default AVLSearch;